import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useProcessMapping, useProcessTasks } from '../../../api/process-client/process-client.hooks';
import type { ProcessTask } from '../../../api/process-client/process-client.type';
import { WorkflowStatus } from '../../../api/process-client/process-client.type';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageHeaderMessage } from '../../../components/page-header-message/page-header-message';
import { Title } from '../../../components/title/title';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { TasksTable } from '../../tasks-table/tasks-table';
import type { ProcessWorkflow } from '../context/process.context.type';
import { getStageTitle } from '../process.helpers';
import { PageContainer } from '../process.styled';
import { formatTasks } from './task-details.helpers';

export function TaskDetails() {
  const i18n = useTranslation();
  useDocumentTitle('Task Details - Bloomfilter');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const teamId = searchParams.get('teamId');
  const boardId = searchParams.get('boardId');
  const workPeriodId = searchParams.get('workPeriodId');
  const taskType = searchParams.get('taskType');
  const epicId = searchParams.get('epicId');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const workflowString = searchParams.get('workflow');
  const workflow: ProcessWorkflow | null = workflowString ? JSON.parse(decodeURIComponent(workflowString)) : null;

  const { taskData: tasks = [], isFetching = false } = useProcessTasks(
    {
      teamId: teamId,
      boardId: boardId,
      workPeriodId: workPeriodId,
      taskTypes: taskType ? [taskType] : null,
      epicId: epicId,
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
      processWorkflow: workflow ?? undefined,
      workflowId: null,
    },
    {
      enabled: Boolean(teamId),
      staleTime: 1000 * 60 * 5,
    },
  );

  // Resolve the workflow id by looking up which workflow owns this board's
  // subproject — the URL only carries `boardId`, not `workflowId`. Then
  // fetch the workflow-scoped `external_section_map` so per-workflow
  // `bucket_label_overrides` reach the heading.
  const organizationId = useGlobalStore((state) => state.organization?.id ?? null);
  const { data: workflows } = useGetWorkflows(organizationId, null);
  const resolvedWorkflowId = useMemo(() => {
    if (!boardId || !workflows) return undefined;
    return workflows.find((w) => w.subprojects?.includes(boardId))?.id;
  }, [boardId, workflows]);
  const { mapping } = useProcessMapping({
    workflowId: resolvedWorkflowId,
    teamId: teamId ?? undefined,
    options: {
      enabled: Boolean(resolvedWorkflowId || teamId),
    },
  });
  const resolvedLabels = mapping?.labels;

  const deduplicateTasks = (tasks: ProcessTask[]) => {
    const uniqueIds: { [key: string]: boolean } = {};
    return tasks.filter((task: ProcessTask) => {
      if (!(task.id in uniqueIds)) {
        uniqueIds[task.id] = true;
        return true;
      } else {
        return false;
      }
    });
  };

  const getHeading = useCallback(() => {
    if (!workflow) {
      return 'Task Details';
    }

    if (workflow.status === WorkflowStatus.Completed) {
      return 'Completed Task Paths';
    }

    return `${workflow.status} from ${getStageTitle(workflow.source, i18n.t, resolvedLabels)} to ${getStageTitle(workflow.destination, i18n.t, resolvedLabels)}`;
  }, [workflow, i18n.t, resolvedLabels]);

  useEffect(() => {
    if (!workflow) {
      navigate('/application/process');
    }
  }, [navigate, workflow]);

  const filteredTasks = deduplicateTasks(tasks);

  const showLocalError = !isFetching && !filteredTasks.length;

  const header = <Title title={getHeading()} />;

  return (
    <MenuDrivenContainer header={header}>
      <BrandedLoadingOverlay visible={isFetching} transitionDuration={30} variant="colored" />
      <PageContainer>
        {showLocalError && (
          <PageHeaderMessage
            message={i18n.t('financials.no_work_units', { work_units: i18n.t('common.work_units') })}
            color="red"
          />
        )}
        <TasksTable tasks={formatTasks(filteredTasks)} />
      </PageContainer>
    </MenuDrivenContainer>
  );
}
